import React from "react";

import { InputField } from "../InputField";
import LabelFor from "../LabelFor/LabelFor";
import { SearchableSelect } from "../SearchableSelect/SearchableSelect";
import { Validation } from "../Validation/Validation.internals";
import { usePhoneInputField } from "./usePhoneInputField";

import type { InputFieldPropsBase } from "../InputField";
import type { PhoneCountry } from "./PhoneCountry";

import * as styles from "./phoneInputField.scss";

export interface PhoneInputFieldProps extends Pick<
	InputFieldPropsBase,
	"value" | "disabled" | "label" | "errors" | "markAsRequired" | "testId" | "onChange"
> {
	defaultIsoCountryCode2: string;
	countries: PhoneCountry[];
	onSelectedCountryChange?: (countryIsoCode2: string, phoneCountryCode: string) => void;
	/** If true, disables automatic phone number formatting and country detection */
	disableFormatting?: boolean;
}

export const PhoneInputField: React.FC<PhoneInputFieldProps> = ({
	value,
	disabled,
	label,
	errors,
	markAsRequired = false,
	defaultIsoCountryCode2,
	countries,
	testId,
	onChange,
	onSelectedCountryChange,
	disableFormatting = false,
}) => {
	const {
		selectedCountry,
		phoneInputId,
		selectOptions,
		searchCountry,
		getSelectedCountryFormatted,
		handlePhoneChange,
		handlePhoneBlur,
		handleCountryChange,
	} = usePhoneInputField({
		value,
		defaultIsoCountryCode2,
		countries,
		onChange,
		onSelectedCountryChange,
		disableFormatting,
	});

	return (
		<>
			<LabelFor htmlFor={phoneInputId} label={label} markAsRequired={markAsRequired} testId={testId} />
			<div className={styles.phoneInputContainer}>
				<div className={styles.searchableSelectWrapper}>
					<SearchableSelect<string>
						selected={selectOptions.find(option => option.value === selectedCountry)}
						onChange={handleCountryChange}
						options={selectOptions}
						disabled={disabled}
						testId={`${testId}_CountrySearchableSelect`}
						selectedFormatter={() => getSelectedCountryFormatted()}
						placeholder="--"
						className={styles.countrySearchableSelect}
						searchFn={searchCountry}
						withoutClearAction
						restoreSelectedOnClear
					/>
				</div>
				<div className={styles.inputFieldWrapper}>
					<InputField
						value={value}
						onChange={handlePhoneChange}
						onBlur={handlePhoneBlur}
						disabled={disabled}
						testId={`${testId}_PhoneNumberInputField`}
						fieldProps={{ id: phoneInputId }}
						inputMode="tel"
					/>
				</div>
			</div>
			<Validation testId={`Validation__Inline_${testId}`} errors={errors?.errors} />
		</>
	);
};
